import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useClient } from '../../context/ClientContext';
import { useTheme } from '../../context/ThemeContext';
import { chatApi, notificationsApi } from '../../lib/api';
import {
  LayoutDashboard,
  FolderKanban,
  CheckSquare,
  Calendar,
  FileText,
  Receipt,
  PieChart,
  Sparkles,
  Users,
  Settings,
  LogOut,
  ChevronLeft,
  Menu,
  ClipboardList,
  MessageSquare,
  BarChart3,
  Trash2,
  Building2,
  TrendingUp,
  DatabaseZap,
  Plug,
  X,
  Bell,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { cn } from '../../lib/utils';

export const Sidebar = ({ collapsed, setCollapsed, onMobileClose, mobileOpen }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { hasPermission, roleDisplayName } = useClient();
  const { theme } = useTheme();
  const [agencyName, setAgencyName] = useState('Marketing Solutions');
  const [agencyLogo, setAgencyLogo] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    const fetchAgency = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/agency-settings`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) { const d = await res.json(); setAgencyName(d.name || 'Marketing Solutions'); setAgencyLogo(d.logo); }
      } catch {}
    };
    fetchAgency();
  }, []);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const r = await chatApi.getUnreadCount();
        setUnreadMessages(r.data.unread_messages || 0);
        setUnreadNotifications(r.data.unread_notifications || 0);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const allNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard', excludeRoles: ['client'] },
    { path: '/clients', icon: Building2, label: 'Clientes', permission: 'clients', excludeRoles: ['client'] },
    { path: '/tasks', icon: CheckSquare, label: 'Tareas', permission: 'tasks', excludeRoles: ['client'] },
    { path: '/content', icon: Calendar, label: 'Contenido', permission: 'content', excludeRoles: ['client'] },
    { path: '/documents', icon: FileText, label: 'Documentos', permission: 'documents', excludeRoles: ['client'] },
    { path: '/work-orders', icon: ClipboardList, label: 'Ordenes de Trabajo', permission: 'work_orders', roles: ['admin', 'client', 'team'] },
    { path: '/chat', icon: MessageSquare, label: 'Chat Interno', permission: 'chat', excludeRoles: ['client'] },
    { path: '/invoices', icon: Receipt, label: 'Facturas', permission: 'invoices', excludeRoles: ['client'] },
    { path: '/finances', icon: PieChart, label: 'Finanzas', permission: 'finances', excludeRoles: ['client'] },
    { path: '/ai-studio', icon: Sparkles, label: 'Estudio IA', permission: 'ai', excludeRoles: ['client'] },
    { path: '/social-insights', icon: TrendingUp, label: 'Insights', permission: 'content', excludeRoles: ['client'] },
    { path: '/trash', icon: Trash2, label: 'Papelera', permission: 'dashboard', excludeRoles: ['client'] },
  ];

  const adminItems = [
    { path: '/performance', icon: BarChart3, label: 'Desempeno', permission: 'performance' },
    { path: '/team', icon: Users, label: 'Equipo', permission: 'team' },
    { path: '/integrations', icon: Plug, label: 'Integraciones', permission: null },
    { path: '/admin/users', icon: DatabaseZap, label: 'Gestionar BD', permission: null },
  ];
  
  // Settings accessible to all users
  const settingsItem = { path: '/settings', icon: Settings, label: 'Mi Cuenta', permission: null };

  const navItems = allNavItems.filter(item => {
    if (item.excludeRoles && item.excludeRoles.includes(user?.role)) return false;
    if (item.roles) return item.roles.includes(user?.role);
    return hasPermission(item.permission);
  });
  const visibleAdminItems = adminItems.filter(item => {
    if (item.permission === null) return user?.role === 'admin';
    return hasPermission(item.permission);
  });

  const isDark = theme === 'dark';

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-full z-50 transition-all duration-300',
        isDark ? 'glass' : 'bg-white/90 backdrop-blur-md border-r border-zinc-200',
        collapsed ? 'w-20' : 'w-64',
        mobileOpen ? 'block' : 'hidden lg:block'
      )}
      data-testid="sidebar"
    >
      <div className="flex flex-col h-full p-4">
        <div className="flex items-center justify-between mb-8">
          {!collapsed && (
            <Link to="/dashboard" className="flex items-center gap-2">
              {agencyLogo && agencyLogo !== 'null' ? (
                <img src={agencyLogo.startsWith('http') ? agencyLogo : `${agencyLogo}`} alt="" className="w-10 h-10 rounded-xl object-cover" onError={(e) => { e.target.style.display='none'; }} />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{agencyName?.charAt(0) || 'M'}</span>
                </div>
              )}
              <span className={cn('font-semibold text-lg', isDark ? 'text-white' : 'text-zinc-900')} data-testid="agency-name">{agencyName}</span>
            </Link>
          )}
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={() => onMobileClose?.()} className={cn('lg:hidden', isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900')}>
              <X size={20} />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)} className={cn('hidden lg:flex', isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900')} data-testid="sidebar-toggle">
              {collapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
            </Button>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const badge = item.path === '/chat' ? unreadMessages : 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => onMobileClose?.()}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all relative',
                  isDark ? 'hover:bg-white/5' : 'hover:bg-zinc-100',
                  isActive && (isDark ? 'bg-orange-500/20 text-orange-400 border-r-2 border-orange-500' : 'bg-orange-50 text-orange-600 border-r-2 border-orange-500'),
                  !isActive && (isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900')
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="relative">
                  <item.icon size={20} />
                  {badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="chat-badge">
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </div>
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                {!collapsed && badge > 0 && (
                  <span className="ml-auto w-5 h-5 bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="chat-badge-text">
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </Link>
            );
          })}

          {visibleAdminItems.length > 0 && (
            <>
              <div className={cn('my-4 border-t', isDark ? 'border-white/5' : 'border-zinc-200')} />
              {visibleAdminItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all',
                      isDark ? 'hover:bg-white/5' : 'hover:bg-zinc-100',
                      isActive && (isDark ? 'bg-orange-500/20 text-orange-400 border-r-2 border-orange-500' : 'bg-orange-50 text-orange-600 border-r-2 border-orange-500'),
                      !isActive && (isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900')
                    )}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <item.icon size={20} />
                    {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </Link>
                );
              })}
            </>
          )}

          {/* Settings - accessible to all users */}
          <div className={cn('my-2 border-t', isDark ? 'border-white/5' : 'border-zinc-200')} />
          <Link
            to={settingsItem.path}
            onClick={() => onMobileClose?.()}
            className={cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all',
              isDark ? 'hover:bg-white/5' : 'hover:bg-zinc-100',
              location.pathname === settingsItem.path && (isDark ? 'bg-orange-500/20 text-orange-400 border-r-2 border-orange-500' : 'bg-orange-50 text-orange-600 border-r-2 border-orange-500'),
              location.pathname !== settingsItem.path && (isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900')
            )}
            data-testid="nav-mi-cuenta"
          >
            <settingsItem.icon size={20} />
            {!collapsed && <span className="text-sm font-medium">{settingsItem.label}</span>}
          </Link>
        </nav>

        <div className={cn('border-t pt-4', isDark ? 'border-white/5' : 'border-zinc-200')}>
          <div className={cn('flex items-center gap-3', collapsed && 'justify-center')}>
            <Avatar className="h-10 w-10 rounded-full">
              <AvatarImage src={user?.picture ? (user.picture.startsWith('http') ? user.picture : `${user.picture}`) : undefined} className="rounded-full object-cover" />
              <AvatarFallback className="bg-orange-500 text-white rounded-full">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm font-medium truncate', isDark ? 'text-white' : 'text-zinc-900')}>{user?.name}</p>
                <p className={cn('text-xs truncate', isDark ? 'text-zinc-500' : 'text-zinc-400')}>{roleDisplayName || user?.role}</p>
              </div>
            )}
            {!collapsed && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className={isDark ? 'text-zinc-400 hover:text-red-400' : 'text-zinc-400 hover:text-red-500'}
                data-testid="logout-btn"
              >
                <LogOut size={18} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
